import { useContext } from 'react';
import { type OneDriveFile } from '../../types';
import SelectedFilesContext from '../../../SelectedFilesContext';
import ClickableItemTableRow from '../ClickableItemTableRow';
import { Checkbox } from '@mui/material';
import FileIcon from '../../../../../../utils/FileIcon';
import FileIconTableCell from '../utils/FileIconTableCell';
import LastModifiedTableCell from '../utils/LastModifiedTableCell';
import SecondaryTableCell from '../utils/SecondaryTableCell';
import FileNameCell from './FileNameCell';
import formatFileSize from './formatFileSize';
import useIsAlreadyAttached from '../utils/useIsAlreadyAttached';
import { ExternalFileSource } from '@/lib/types';
import { FALLBACK_MIME_TYPE } from '../../../../utils/constants';

type OneDriveFileTableRowProps = {
  file: OneDriveFile;
};

const OneDriveFileTableRow = ({ file }: OneDriveFileTableRowProps) => {
  const { selectedFiles, selectFile, deselectFile, canSelectMoreFiles } =
    useContext(SelectedFilesContext);

  const fileUrl = file.webUrl || null;
  const isSelected = fileUrl !== null && selectedFiles.has(fileUrl);

  const isAlreadyAttached = useIsAlreadyAttached(file);

  const handleSelectToggle = () => {
    if (fileUrl === null) {
      return;
    }

    if (isSelected) {
      deselectFile(fileUrl);

      return;
    }

    selectFile({
      source: ExternalFileSource.MICROSOFT_ONEDRIVE,
      externalId: file.id,
      filename: file.name,
      url: fileUrl,
      mimeType: file.file?.mimeType ?? FALLBACK_MIME_TYPE,
    });
  };

  return (
    <ClickableItemTableRow
      selected={isSelected || isAlreadyAttached}
      onClick={handleSelectToggle}
      disabled={isAlreadyAttached || (!isSelected && !canSelectMoreFiles)}
    >
      <td>
        <Checkbox
          checked={isSelected || isAlreadyAttached}
          onChange={handleSelectToggle}
          disabled={
            fileUrl === null ||
            isAlreadyAttached ||
            (!isSelected && !canSelectMoreFiles)
          }
        />
      </td>
      <FileIconTableCell>
        {typeof file.file !== 'undefined' && (
          <FileIcon filename={file.name} mimeType={file.file.mimeType} />
        )}
      </FileIconTableCell>
      <FileNameCell file={file} />
      <LastModifiedTableCell oneDriveItem={file} />
      <SecondaryTableCell>{formatFileSize(file.size)}</SecondaryTableCell>
    </ClickableItemTableRow>
  );
};

export default OneDriveFileTableRow;
